import { useState } from 'react';
import { FormField } from '../../../school_panel/components/ui';
import { fieldClass } from '../../../school_panel/lib/fieldClass';
import { FormFooter, type FieldErrors, type FormProps, SourceFields, useQuestionSource } from './shared';

export const WRITTEN_HINT = 'AI suggests a mark from the model answer; you confirm every mark.';

export default function WrittenForm({ initial, saving, errors, onSave, onCancel, chapters }: FormProps) {
  const [text, setText] = useState(initial?.text ?? '');
  const [maxMarks, setMaxMarks] = useState(String(initial?.max_marks ?? 2));
  const [model, setModel] = useState(initial?.model_answer ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [explanation, setExplanation] = useState(initial?.explanation ?? '');
  const source = useQuestionSource(initial);
  const [local, setLocal] = useState<FieldErrors>({});
  const e = { ...local, ...errors };

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const err: FieldErrors = {};
    const mm = Number(maxMarks);
    if (!text.trim()) err.text = 'Write the question.';
    if (!maxMarks.trim() || !Number.isInteger(mm) || mm < 1 || mm > 10) err.max_marks = 'Max marks must be 1–10.';
    if (!model.trim()) err.model_answer = 'Add a model answer.';
    else if (model.trim().length > 2000) err.model_answer = 'Add a model answer (up to 2000 characters).';
    if (notes.trim().length > 1000) err.notes = 'Marking notes must be up to 1000 characters.';
    setLocal(err);
    if (Object.keys(err).length) return;
    onSave({
      type: 'written', text: text.trim(), max_marks: mm, model_answer: model.trim(), notes: notes.trim(),
      ...(explanation.trim() ? { explanation: explanation.trim() } : {}), ...source.payload,
    });
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <FormField label="Question" htmlFor="wr-text" required error={e.text}>
        <textarea id="wr-text" className={fieldClass} rows={3} value={text} onChange={(x) => setText(x.target.value)} />
      </FormField>
      <FormField label="Max marks" htmlFor="wr-max" required error={e.max_marks}>
        <input id="wr-max" type="number" min={1} max={10} step={1} className={fieldClass}
          value={maxMarks} onChange={(x) => setMaxMarks(x.target.value)} />
      </FormField>
      <FormField label="Model answer" htmlFor="wr-model" required error={e.model_answer}>
        <textarea id="wr-model" className={fieldClass} rows={4} maxLength={2000} value={model}
          onChange={(x) => setModel(x.target.value)} />
      </FormField>
      <FormField label="Marking notes for AI (optional)" htmlFor="wr-notes" error={e.notes}>
        <textarea id="wr-notes" className={fieldClass} rows={2} maxLength={1000} value={notes}
          onChange={(x) => setNotes(x.target.value)} />
      </FormField>
      <p className="text-sm text-muted">{WRITTEN_HINT}</p>
      <FormField label="Explanation (optional)" htmlFor="wr-expl" error={e.explanation}>
        <textarea id="wr-expl" className={fieldClass} rows={2} value={explanation}
          onChange={(x) => setExplanation(x.target.value)} />
      </FormField>
      <SourceFields source={source} chapters={chapters} errors={e} idPrefix="wr" />
      <FormFooter saving={saving} onCancel={onCancel} />
    </form>
  );
}
